import React, { useEffect, useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import DataTableComponent, { DataTableProps } from './DataTableComponent';
import { OrderDirection } from '../common/enums/order-direction.enum';
import { useErrorToast, useSuccessToast } from '../hooks/useToast';
import Card from '../layouts/CardLayout';
import LoadingContent from './LoadingContent';
import { SelectItem } from './Select';
import CellLayout from './CellLayout';
import PageHeader from './PageHeader';
import PageLayout from '../layouts/PageLayout';
import Popover from './Popover';
import { useTranslation } from 'react-i18next';
import { ProfileStatus, IProfile } from '../common/interfaces/profile.interface';
import DataTableFilters from './DataTableFilters';
import i18n from '../common/config/i18n';
import {
  ProfileStatusBackgroundColorMapper,
  ProfileStatusMarkerColorMapper,
} from '../common/utils/utils';
import Chip from './Chip';
import StatusWithMarker from './StatusWithMarker';
import Button from './Button';
import {
  useAddProfileMutation,
  useDeleteProfileMutation,
  useProfilesQuery,
  useUpdateProfileMutation,
} from '../services/profile/profile.service';
import ProfileStatusSelect from './ProfileStatusSelect';
import ProfileModal from './ProfileModal';
import { ProfileTableBasicProps } from './ProfilesTableWithQueryParams';
import ConfirmationModal from './ConfirmationModal';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { UserRoleEnum } from '../common/interfaces/user-profile.interface';
import { useUserProfile } from '../contexts/user/UserContext';
import { useParams } from 'react-router-dom';

const ProfileTableFilteredHeader: TableColumn<IProfile>[] = [
  {
    id: 'email',
    name: 'Email',
    grow: 1,
    sortable: true,
    cell: (row: IProfile) => (
      <CellLayout>
        <small id={`profile_email__${row.id}`} className="cell-layout">
          {row.email}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'phone',
    name: 'Telefon',
    sortable: true,
    grow: 1,
    cell: (row: IProfile) => (
      <CellLayout>
        <small id={`profile_phone__${row.id}`} className="cell-layout">
          {row.phone}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'details',
    name: 'Detalii',
    sortable: true,
    grow: 1,
    cell: (row: IProfile) => (
      <CellLayout>
        <small id={`profile_details__${row.id}`} className="cell-layout">
          {row.details}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'role',
    name: 'Rol',
    sortable: true,
    grow: 1,
    cell: (row: IProfile) => (
      <CellLayout>
        <small id={`profile_role__${row.id}`} className="cell-layout">
          {i18n.t(`roles:${row.role}`)}
        </small>
      </CellLayout>
    ),
  },
  {
    id: 'status',
    name: 'Status',
    sortable: true,
    grow: 1,
    cell: (row: IProfile) => (
      <CellLayout>
        <Chip bgColor={ProfileStatusBackgroundColorMapper[row.status]}>
          <StatusWithMarker
            markerColour={ProfileStatusMarkerColorMapper[row.status]}
            textColour={ProfileStatusBackgroundColorMapper[row.status]}
            id={`profile_status__${row.id}`}
          >
            {i18n.t(`profiles:status.${row.status}`)}
          </StatusWithMarker>
        </Chip>
      </CellLayout>
    ),
  },
];

type ProfilesTableType<T extends object> = React.FC<ProfileTableBasicProps> & {
  DataTableComponent: React.FC<DataTableProps<T>>;
};

const ProfilesTable: ProfilesTableType<IProfile> = ({
  query,
  setQuery,
}: ProfileTableBasicProps) => {
  const { companyId } = useParams();

  const [selectedProfile, setSelectedProfile] = useState<IProfile | null>(null);
  const [isAddProfileModalOpen, setIsAddProfileModalOpen] = useState<boolean>(false);
  const [isEditProfileModalOpen, setIsEditProfileModalOpen] = useState<boolean>(false);
  const [isConfirmationSaveOpen, setIsConfirmationSaveOpen] = useState<boolean>(false);

  const [selectedDeleteProfile, setSelectedDeleteProfile] = useState<IProfile | null>(null);

  const { mutateAsync: addProfile, isPending: isAddingProfile } = useAddProfileMutation();
  const { mutateAsync: updateProfile, isPending: isUpdatingProfile } = useUpdateProfileMutation();
  const { mutateAsync: deleteProfile, isPending: isDeletingProfile } = useDeleteProfileMutation();

  const { user } = useUserProfile();

  const [status, setStatus] = useState<SelectItem<string>>();
  const { t } = useTranslation();

  const currentCompanyId = useMemo(() => {
    if (user?.role === UserRoleEnum.SUPER_ADMIN && companyId) {
      return companyId;
    } else if (user?.company_id) {
      return `${user?.company_id}`;
    } else {
      return '';
    }
  }, [user, companyId]);

  const {
    data: profiles,
    isLoading: isProfilesLoading,
    error: profilesError,
    refetch,
  } = useProfilesQuery(
    query?.limit as number,
    query?.page as number,
    query.search,
    query.orderBy,
    query.orderDirection as OrderDirection,
    query.status,
    currentCompanyId,
  );

  useEffect(() => {
    if (profilesError) {
      useErrorToast('Eroare la preluarea datelor.');
    }
  }, [profilesError]);

  const buildProfileActionColumn = (): TableColumn<IProfile> => {
    const openProfileMenuItems = [
      {
        label: 'Editeaza',
        onClick: handleOnEdit,
        itemId: 'request_open__kebab_button',
      },
      {
        label: 'Sterge',
        onClick: handleOnDelete,
        itemId: 'request_delete__kebab_button',
      },
    ];

    return {
      name: '',
      cell: (row: IProfile) => (
        <Popover<IProfile>
          row={row}
          items={openProfileMenuItems}
          popoverId={`profile_${row.id}__kebab_menu`}
          className="right-0"
        />
      ),
      width: '60px',
      // allowOverflow: true,
    };
  };

  const handleOnEdit = (row: IProfile) => {
    setSelectedProfile(row);
    setIsEditProfileModalOpen(true);
  };

  const onChangeRowsPerPage = (limit: number) => {
    setQuery({ limit, page: 1 }, 'replaceIn');
  };

  const onChangePage = (page: number) => {
    setQuery({ page }, 'replaceIn');
  };

  const onSort = (column: TableColumn<IProfile>, direction: SortOrder) => {
    setQuery(
      {
        orderBy: column.id as string,
        orderDirection:
          direction.toLocaleUpperCase() === OrderDirection.ASC
            ? OrderDirection.ASC
            : OrderDirection.DESC,
      },
      'replaceIn',
    );
  };

  const onSearch = (search: string) => {
    setQuery({ search }, 'replaceIn');
  };

  const onResetFilters = () => {
    setStatus(undefined);
    setQuery({}, 'push');
  };

  const onStatusChange = (statusItem: SelectItem<string>) => {
    setStatus(statusItem);
    setQuery({ status: statusItem.key as ProfileStatus }, 'replaceIn');
  };

  /* eslint-disable  @typescript-eslint/no-explicit-any */
  const handleOnAddRequest = async (data: any) => {
    const company_id = user?.role === UserRoleEnum.SUPER_ADMIN && companyId ? +companyId : null;
    addProfile(
      { ...data, role: data.role.key, company_id: company_id },
      {
        onSuccess: () => {
          refetch();
          setIsAddProfileModalOpen(false);
        },
        onError: () => {
          useErrorToast('Eroare la adăugarea profilului');
        },
      },
    );
  };

  const handleOnEditRequest = async (data: any) => {
    const status = data.status === 'true' ? ProfileStatus.ACTIVE : ProfileStatus.INACTIVE;
    updateProfile(
      {
        ...data,
        status,
        role: data.role.key,
      },
      {
        onSuccess: () => {
          refetch();
          setIsEditProfileModalOpen(false);
        },
        onError: () => {
          useErrorToast('Eroare la actualizarea profilului');
        },
      },
    );
  };

  const handleDeleteProfile = async () => {
    if (!selectedDeleteProfile) {
      return;
    }

    deleteProfile(selectedDeleteProfile?.id, {
      onSuccess: () => {
        useSuccessToast('Utilizatorul a fost șters cu succes');
        refetch();
      },
      onError: () => {
        useErrorToast('Eroare la ștergerea profilului');
      },
    });
  };

  const handleOnDelete = (row: IProfile) => {
    setSelectedDeleteProfile(row);
    setIsConfirmationSaveOpen(true);
  };

  const onConfirmationSaveClose = () => {
    setSelectedDeleteProfile(null);
    setIsConfirmationSaveOpen(false);
  };

  return (
    <PageLayout>
      <div className="flex gap-4 justify-between w-full">
        <PageHeader title="Utilizatori" />
        <Button
          id="profile_add__button"
          className="btn-primary"
          onClick={setIsAddProfileModalOpen.bind(null, true)}
          label={'Adauga utilizator'}
          loading={isAddingProfile || isUpdatingProfile || isDeletingProfile}
        />
      </div>
      <Card>
        <DataTableFilters
          onSearch={onSearch}
          onResetFilters={onResetFilters}
          searchValue={query.search}
        >
          <ProfileStatusSelect
            id="profile_status_filter__select"
            onChange={onStatusChange}
            selected={status}
            className="max-h-52"
          />
        </DataTableFilters>
        <ProfilesTable.DataTableComponent
          columns={
            [...ProfileTableFilteredHeader, buildProfileActionColumn()] as TableColumn<IProfile>[]
          }
          pagination
          onSort={onSort}
          data={profiles?.items}
          onChangePage={onChangePage}
          onChangeRowsPerPage={onChangeRowsPerPage}
          pointerOnHover={true}
          paginationDefaultPage={query.page}
          paginationPerPage={query.limit}
          paginationTotalRows={profiles?.meta.totalItems}
          defaultSortAsc={false}
          loading={isProfilesLoading}
        />
        {isProfilesLoading && <LoadingContent />}
      </Card>
      {isAddProfileModalOpen && (
        <ProfileModal
          onClose={setIsAddProfileModalOpen.bind(null, false)}
          onSubmit={handleOnAddRequest}
          title={t('profile_modal:form_name')}
          buttonLabel={t('profile_modal:add')}
          isLoading={isAddingProfile}
        />
      )}
      {isEditProfileModalOpen && (
        <ProfileModal
          onClose={setIsEditProfileModalOpen.bind(null, false)}
          onSubmit={handleOnEditRequest}
          title={t('profile_modal:form_name')}
          buttonLabel={t('profile_modal:edit')}
          profile={selectedProfile}
        />
      )}
      {isConfirmationSaveOpen && (
        <ConfirmationModal
          onConfirm={() => {
            setIsConfirmationSaveOpen(false);
            handleDeleteProfile();
          }}
          onClose={onConfirmationSaveClose}
          icon={<ExclamationCircleIcon className="text-yellow h-16 w-14" />}
          confirmButtonClass="btn-primary"
          title={'Confirmare ștergere utilizator'}
          confirmButtonLabel={'Sterge'}
        />
      )}
    </PageLayout>
  );
};

ProfilesTable.DataTableComponent = DataTableComponent;

export default ProfilesTable;
